import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("br", null, null, -1)

import BigNumber from 'bignumber.js';
import { computed, ComputedRef, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { TokenInfo, Portfolio } from '@/sdk/entities/portfolio';
import TooltipIcon from '@/components/tooltip/TooltipIcon.vue';
import { compareTokenAddresses } from '@/sdk/utils';
import { BIG_ZERO } from '@/utils/bigNumber';
import { USD_DECIMAL_PRECISION } from '@/constants/INTERFACE_SETTINGS';
import NoticeListWrapper from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/NoticeListWrapper.vue';
import BuyTokensNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/BuyTokensNotice.vue';
import DecreaseAmountNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/DecreaseAmountNotice.vue';
import SelectAnyTokensNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/SelectAnyTokensNotice.vue';
import SelectTokensNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/SelectTokensNotice.vue';
import SwitchOffBalancesModeNotice from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/common/notices/SwitchOffBalancesModeNotice.vue';
import { LOSS } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/LOSS';
import { EasyModeAddLiquidityForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-form';
import { EasyModeAddLiquidityToken } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/easy-mode-token';
import { AddTokenNotice } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/models/token-notice';
import { USD_TOKEN } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/common/constants/USD_TOKEN';
import { useEasyModeForm } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeForm';
import { useEasyModeAddLiquidityBalanceErrors } from '@/views/pages/liquidity/portfolios/portfolio/liquidity-management/easy-mode/composables/useEasyModeAddLiquidityBalanceErrors';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddLiquidityNoticesList',
  props: {
    portfolio: null
  },
  setup(__props: any) {

const props = __props as {
  portfolio: Portfolio;
}

const LOGGER = {
  groupCollapsed: (...label: any[]) => {
    if (isLoggingDisabled()) return;

    console.groupCollapsed(...label);
  },
  groupEnd: () => {
    if (isLoggingDisabled()) return;

    console.groupEnd();
  },
  log: (message?: any, ...optionalParams: any[]) => {
    if (isLoggingDisabled()) return;

    console.log(message, ...optionalParams);
  },
};



const { t } = useI18n();
const {
  easyModeForm: easyModeFormAddLiquidity,
  lossType,
  getTokenInfoByFormToken,
  selectedTokens: addLiquiditySelectedTokens,
} = useEasyModeForm();
const easyModeForm = easyModeFormAddLiquidity as EasyModeAddLiquidityForm;
const selectedTokens = addLiquiditySelectedTokens as ComputedRef<EasyModeAddLiquidityToken[]>;

const translationPath = 'portfolio.easyModeTranslations.addLiquidity.noticesList';

/* template data */
const color = ref<'info' | 'warning'>('warning');

const mainHeader = ref('');
const mainTooltip = ref('');
const applyHeader = ref('');

const tokensToBuy = ref<{ amount: BigNumber; symbol: string }[] | null>(null);
const tokensToSelect = ref<TokenInfo[] | null>(null);
const showSelectAnyTokensNotice = ref<boolean>(false);
const decreaseAmount = ref<{ toAmount: BigNumber; symbol: string } | null>(null);
const showSwitchOffBalancesNotice = ref<boolean>(false);

const showBuyTokensNotice = computed(() => !!tokensToBuy.value && tokensToBuy.value.length > 0);
const showSelectTokensNotice = computed(
  () => !!tokensToSelect.value && tokensToSelect.value.length > 0,
);
const showDecreaseAmountNotice = computed(() => !!decreaseAmount.value);

const emptyApplyBody = computed(
  () =>
    applyHeader.value &&
    !(
      showBuyTokensNotice.value ||
      showSelectTokensNotice.value ||
      showSelectAnyTokensNotice.value ||
      showDecreaseAmountNotice.value ||
      showSwitchOffBalancesNotice.value
    ),
);

const shouldShowBlock = computed(
  () => easyModeForm.baseTokenAmount.gt(0) && mainHeader.value && !emptyApplyBody.value,
);

// to keep template typing working
const _tokensToBuy = computed(() => tokensToBuy.value as { amount: BigNumber; symbol: string }[]);
const _tokensToSelect = computed(() => tokensToSelect.value as TokenInfo[]);
const _decreaseAmount = computed(
  () => decreaseAmount.value as { toAmount: BigNumber; symbol: string },
);

/* computed properties for imperative calculation */

const {
  allBalancesSumInBaseToken,
  selectedBalancesSumInBaseToken,
  isNotEnoughBalance,
  isNotEnoughBalanceOfSelectedTokens,
} = useEasyModeAddLiquidityBalanceErrors();

const selectedLimitsSum = computed(() => {
  LOGGER.groupCollapsed(`selectedLimitsSum in ${easyModeForm.portfolio.baseToken.symbol}`);
  const sum = selectedTokens.value.reduce((acc, token) => {
    const tokenInfo = getTokenInfoByFormToken(token);
    const baseTokenEquivalent = tokenInfo.getDepositPriceRelative().multipliedBy(token.noLossLimit);

    loggingTokenLimit(tokenInfo, baseTokenEquivalent, token);

    return acc.plus(baseTokenEquivalent);
  }, BIG_ZERO);
  LOGGER.log('selectedLimitsSum : ', sum.toString());
  LOGGER.groupEnd();
  return sum;
});
const noTokensSelected = computed(() => easyModeForm.tokens.every(token => !token.checked));
const totalGTSelectedLimitsSum = computed(() => {
  const precision = props.portfolio.baseToken.decimals;
  return easyModeForm.baseTokenAmount.dp(precision).gt(selectedLimitsSum.value.dp(precision));
});
const feeCallReverted = computed(() => easyModeForm.impactError);
const isProvideWithinBalances = computed(() => easyModeForm.withinBalances);

const resetRefs = () => {
  color.value = 'warning';

  mainHeader.value = '';
  mainTooltip.value = '';
  applyHeader.value = '';

  tokensToBuy.value = null;
  tokensToSelect.value = null;
  showSelectAnyTokensNotice.value = false;
  decreaseAmount.value = null;
  showSwitchOffBalancesNotice.value = false;
};

watchEffect(() => {
  resetRefs();

  if (allBalancesSumInBaseToken.value.eq(0)) {
    setWarningColor();
    setHeader('youDontHavePortfolioTokens');
    setSimpleApplyHeader();

    if (isProvideWithinBalances.value) {
      showSwitchOffBalancesNotice.value = true;
      return;
    }

    handleWithinBalancesFalseInputAmountError();
    return;
  }

  if (noTokensSelected.value) {
    setInfoColor();
    setHeader('noSelectedTokens');
    setEmptyApplyHeader();
    return;
  }

  loggingAllBalancesSum();

  if (isNotEnoughBalance.value) {
    setWarningColor();
    setHeader('balanceIsNotEnough');
    setSimpleApplyHeader();

    if (isProvideWithinBalances.value) {
      setDecreaseAmountToByBaseTokenValue(allBalancesSumInBaseToken.value);
      showSwitchOffBalancesNotice.value = true;
      return;
    }

    handleWithinBalancesFalseInputAmountError();
    return;
  }

  loggingSelectedBalancesSum();

  if (isNotEnoughBalanceOfSelectedTokens.value) {
    setWarningColor();
    setHeader('balanceOfSelectedIsNotEnough');
    setSimpleApplyHeader();

    if (isProvideWithinBalances.value) {
      showSelectAnyTokensNotice.value = true;
      setDecreaseAmountToByBaseTokenValue(selectedBalancesSumInBaseToken.value);
      showSwitchOffBalancesNotice.value = true;
      return;
    }

    handleWithinBalancesFalseInputAmountError();
    return;
  }

  if (feeCallReverted.value) {
    setWarningColor();
    setHeaderAndTooltip('callReverted');
    setMinimiseLossApplyHeader();

    if (isProvideWithinBalances.value) {
      showSwitchOffBalancesNotice.value = true;
      return;
    }

    setTokensToSelect();

    if (!totalGTSelectedLimitsSum.value) {
      setBuyTokens();
      return;
    }
    return;
  }

  if (lossType.value === LOSS.SMALL) {
    setInfoColor();
    setHeaderAndTooltip('smallLoss');
    setMinimiseLossApplyHeader();

    setTokensToSelect();

    if (isProvideWithinBalances.value) {
      showSwitchOffBalancesNotice.value = true;
      return;
    }

    handleWithinBalancesFalseInputAmountError();
    return;
  }

  if (lossType.value === LOSS.HUGE) {
    setWarningColor();
    setHeaderAndTooltip('hugeLoss');
    setMinimiseLossApplyHeader();

    setTokensToSelect();

    const notDistributedAfterFirstIterationAmount = easyModeForm.baseTokenAmount.minus(
      easyModeForm.restAmountToDistributeAfterFirstIteration,
    );

    if (notDistributedAfterFirstIterationAmount.gt(0)) {
      setDecreaseAmountToByBaseTokenValue(notDistributedAfterFirstIterationAmount);
    }

    if (isProvideWithinBalances.value) {
      showSwitchOffBalancesNotice.value = true;
      return;
    }

    handleWithinBalancesFalseInputAmountError();
  }
});

function handleWithinBalancesFalseInputAmountError(): void {
  loggingSelectedLimitsSum();

  if (totalGTSelectedLimitsSum.value) {
    setTokensToSelect();

    if (selectedLimitsSum.value.gt(0)) {
      setDecreaseAmountToByBaseTokenValue(selectedLimitsSum.value);
    }
  } else {
    setBuyTokens();
  }
}

function setTokensToSelect(): void {
  const formTokensToSelect = easyModeForm.tokens.filter(
    token => !token.checked && !token.disabled && token.noLossLimit.gt(0),
  );
  tokensToSelect.value = formTokensToSelect.map(getTokenInfoByFormToken);
}

function setBuyTokens(): void {
  tokensToBuy.value = selectedTokens.value
    .filter(formToken => formToken.notice?.type === 'add')
    .map(formToken => {
      const tokenInfo = getTokenInfoByFormToken(formToken);

      return {
        symbol: tokenInfo.token.unwrapWETH().symbol!,
        amount: (formToken.notice as AddTokenNotice).amount,
      };
    });
}

function setDecreaseAmountToByBaseTokenValue(baseTokenValue: BigNumber): void {
  const symbol = easyModeForm.selectedCurrencySymbol;
  let toAmount = baseTokenValue;

  if (symbol === USD_TOKEN.symbol) {
    toAmount = easyModeForm.portfolio.priceInUSD
      .multipliedBy(baseTokenValue)
      .decimalPlaces(USD_DECIMAL_PRECISION, BigNumber.ROUND_DOWN);

    if (toAmount.isEqualTo(0)) {
      return;
    }
  }

  decreaseAmount.value = { symbol, toAmount };
}

function setHeader(name: string) {
  mainHeader.value = t(`${translationPath}.headers.${name}`);
}

function setHeaderAndTooltip(name: string) {
  mainHeader.value = t(`${translationPath}.headers.${name}.text`);
  mainTooltip.value = t(`${translationPath}.headers.${name}.tooltip`);
}

function setSimpleApplyHeader() {
  applyHeader.value = t(`${translationPath}.headers.applyChanges`);
}

function setMinimiseLossApplyHeader() {
  applyHeader.value = t(`${translationPath}.headers.applyChangesToMinimise`);
}

function setEmptyApplyHeader() {
  applyHeader.value = '';
}

function setWarningColor() {
  color.value = 'warning';
}

function setInfoColor() {
  color.value = 'info';
}

const onSelectToken = (tokenInfo: TokenInfo): void => {
  const formToken = easyModeForm.tokens.find(token =>
    compareTokenAddresses(tokenInfo.token.address, token.address),
  )!;

  formToken.checked = true;
};

const onSwitchOffWithinBalances = (): void => {
  easyModeForm.withinBalances = false;
};

const onDecreaseAmount = ({ toAmount, symbol }: { toAmount: BigNumber; symbol: string }): void => {
  let newBaseTokenAmount = toAmount;

  if (symbol === USD_TOKEN.symbol) {
    newBaseTokenAmount = toAmount.div(easyModeForm.portfolio.priceInUSD);
  }

  easyModeForm.baseTokenAmount = newBaseTokenAmount;
};

// DEBUG

function isLoggingDisabled() {
  return !window['BLUESHIFT_DEBUG'].EASY_MODE_ADD_LIQUIDITY;
}

function loggingTokenLimit(
  tokenInfo: TokenInfo,
  baseTokenEquivalent: BigNumber,
  token: EasyModeAddLiquidityToken,
) {
  if (isLoggingDisabled()) return;

  console.groupCollapsed(
    `[${tokenInfo.token.symbol}] limit [in  ${tokenInfo.baseToken.symbol}]: `,
    baseTokenEquivalent.toString(),
  );
  console.log(
    `Deposit price [${tokenInfo.token.symbol} in ${tokenInfo.baseToken.symbol}]: `,
    tokenInfo.getDepositPriceRelative().toString(),
  );
  console.log(`No loss limit [${tokenInfo.token.symbol}] : `, token.noLossLimit.toString());
  console.groupEnd();
}

function loggingAllBalancesSum() {
  if (isLoggingDisabled()) return;

  console.groupCollapsed('[ADD LIQUIDITY] Amount > AllBalancesSum : ', isNotEnoughBalance.value);
  console.log('easyModeForm.baseTokenAmount : ', easyModeForm.baseTokenAmount.toString());
  console.log('allBalancesSum : ', allBalancesSumInBaseToken.value.toString());
  console.groupEnd();
}

function loggingSelectedBalancesSum() {
  if (isLoggingDisabled()) return;

  console.groupCollapsed(
    '[ADD LIQUIDITY] Amount > SelectedBalancesSum : ',
    isNotEnoughBalanceOfSelectedTokens.value,
  );
  console.log('easyModeForm.baseTokenAmount : ', easyModeForm.baseTokenAmount.toString());
  console.log('selectedBalancesSum : ', selectedBalancesSumInBaseToken.value.toString());
  console.groupEnd();
}

function loggingSelectedLimitsSum() {
  if (isLoggingDisabled()) return;

  console.groupCollapsed(
    '[ADD LIQUIDITY] Amount > SelectedLimitsSum : ',
    totalGTSelectedLimitsSum.value,
  );
  console.log('easyModeForm.baseTokenAmount : ', easyModeForm.baseTokenAmount.toString());
  console.log('selectedLimitsSum : ', selectedLimitsSum.value.toString());
  console.groupEnd();
}

return (_ctx: any,_cache: any) => {
  return (_unref(shouldShowBlock))
    ? (_openBlock(), _createBlock(NoticeListWrapper, {
        key: 0,
        color: color.value
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", null, [
            _createTextVNode(_toDisplayString(mainHeader.value) + " ", 1),
            (mainTooltip.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createVNode(TooltipIcon, {
                    tooltip: mainTooltip.value,
                    color: color.value,
                    "no-space": "",
                    class: "ml-s1",
                    inline: ""
                  }, null, 8, ["tooltip", "color"]),
                  _hoisted_1
                ], 64))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(applyHeader.value), 1)
          ]),
          (_unref(showBuyTokensNotice))
            ? (_openBlock(), _createBlock(BuyTokensNotice, {
                key: 0,
                "tokens-to-add": _unref(_tokensToBuy)
              }, null, 8, ["tokens-to-add"]))
            : _createCommentVNode("", true),
          (_unref(showSelectTokensNotice))
            ? (_openBlock(), _createBlock(SelectTokensNotice, {
                key: 1,
                "tokens-to-select": _unref(_tokensToSelect),
                onSelectToken: onSelectToken
              }, null, 8, ["tokens-to-select"]))
            : _createCommentVNode("", true),
          (showSelectAnyTokensNotice.value)
            ? (_openBlock(), _createBlock(SelectAnyTokensNotice, { key: 2 }))
            : _createCommentVNode("", true),
          (_unref(showDecreaseAmountNotice))
            ? (_openBlock(), _createBlock(DecreaseAmountNotice, {
                key: 3,
                "to-amount": _unref(_decreaseAmount).toAmount,
                symbol: _unref(_decreaseAmount).symbol,
                onClick: onDecreaseAmount
              }, null, 8, ["to-amount", "symbol"]))
            : _createCommentVNode("", true),
          (showSwitchOffBalancesNotice.value)
            ? (_openBlock(), _createBlock(SwitchOffBalancesModeNotice, {
                key: 4,
                color: color.value,
                onClick: onSwitchOffWithinBalances
              }, null, 8, ["color"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["color"]))
    : _createCommentVNode("", true)
}
}

})